/** High-intent SEO guides / blog-style pages. */
import { PRICING_COPY } from './pricingCopy.js';

export const GUIDE_PAGE_ORDER = [
  'contractors-near-me',
  'patio-cover-cost',
  'glass-vs-aluminum',
  'permit',
  'rain',
  'install-timeline',
  'fraser-valley',
  'coastal',
  'tri-cities',
  'patio-cover-cost-abbotsford',
  'patio-cover-cost-surrey',
  'patio-cover-cost-burnaby',
  'patio-cover-cost-langley',
  'patio-cover-cost-richmond',
  'patio-cover-cost-coquitlam',
  'patio-cover-cost-delta',
  'patio-cover-cost-north-vancouver',
  'patio-cover-cost-west-vancouver',
  'patio-cover-cost-new-westminster',
  'patio-cover-cost-maple-ridge',
  'patio-cover-cost-white-rock',
  'patio-cover-cost-pitt-meadows',
  'patio-cover-cost-port-coquitlam',
  'patio-cover-cost-port-moody',
  'showroom-vancouver',
  'showroom-burnaby',
  'showroom-near-me',
  'aluminum-showroom',
  'glass-showroom',
  'sunroom-showroom',
  'showroom-new-westminster',
  'showroom-coquitlam',
  'showroom-richmond',
  'showroom-surrey',
];

export const GUIDE_PAGES = {
  'contractors-near-me': {
    id: 'contractors-near-me',
    path: '/patio-cover-contractors-near-me',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Contractors Near Me — Price Yours in 60 Seconds | LoomiHome Patios',
    metaDescription:
      'Looking for patio cover installers near you in Metro Vancouver? Design your cover online and see your price in 60 seconds — aluminum, glass, or combo — then book a free on-site measurement.',
    h1: 'Patio Cover Contractors Near Me — Metro Vancouver',
    intro:
      'If you searched “patio cover contractors near me” or “patio cover installers near me,” you probably want two things: a trustworthy local team and a clear price range before booking visits. LoomiHome serves Metro Vancouver and the Lower Mainland — Vancouver, Burnaby, Richmond, Surrey, Delta, Coquitlam, Port Coquitlam, Port Moody, Langley, North Vancouver, West Vancouver, New Westminster, Maple Ridge, Pitt Meadows, Abbotsford, White Rock, and nearby areas. Start with an online planning range from our Design & Price tool (about 60 seconds), then book a free on-site measurement when the range fits your budget.',
    sections: [
      {
        h2: 'How to compare patio cover contractors without wasting evenings',
        body: 'Call three contractors and you may get three different formats, timelines, and vague “starting from” numbers. A structured ballpark first — city, approximate size, product type — lets you filter who is worth a site visit.',
      },
      {
        h2: 'What to look for in a local patio cover contractor',
        body: 'Clear product options (aluminum, glass, combo, sunroom), measured final quotes, drainage and attachment details, and photos of completed local work. For BC homes, rain performance matters as much as looks.',
      },
      {
        h2: 'Cities we serve in Metro Vancouver',
        body: 'We regularly work in Vancouver, Burnaby, Richmond, Surrey, Delta, Coquitlam, Port Coquitlam, Port Moody, Langley, North Vancouver, West Vancouver, New Westminster, Maple Ridge, Pitt Meadows, Abbotsford, and White Rock. If you are on the edge of the Lower Mainland, ask in chat — we can usually confirm quickly.',
      },
      {
        h2: 'Installer vs contractor — what homeowners usually mean',
        body: 'Most people use both terms for the same goal: someone local who can price, supply, and install a patio cover correctly. Our flow covers estimate, measurement, and installation planning in one place.',
      },
    ],
    pricingNote:
      'Use the homepage Design & Price tool with your city, rough patio size, and cover type to get a planning range before anyone visits your home.',
    faqs: [
      {
        q: 'Do you serve patio cover searches from Vancouver and nearby cities?',
        a: 'Yes — Metro Vancouver and the broader Lower Mainland are our primary service area. We are a BC team, not a US contractor listing.',
      },
      {
        q: 'How fast can I get a quote from a patio cover contractor?',
        a: 'Rough range in about a minute in the online Design & Price tool. Your formal quote is confirmed after a free on-site measurement.',
      },
      {
        q: 'What patio cover types can I compare?',
        a: 'Aluminum, glass, skyline combo, and sunroom directions — helpful if you are still deciding product type.',
      },
      {
        q: 'How much does a patio cover usually cost in Metro Vancouver?',
        a: PRICING_COPY.threeWayShort,
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Finished aluminum patio cover on a Burnaby backyard patio, Lower Mainland project',
      caption:
        'Recent Burnaby aluminum patio cover — approx. 24×20 ft rain-ready backyard cover. Use the online Design & Price tool to see a planning range for a similar size, then book a free measurement.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-contractor-vancouver', label: 'Patio cover contractor — Vancouver' },
      { path: '/patio-cover-contractor-richmond', label: 'Patio cover contractor — Richmond' },
      { path: '/patio-cover-contractor-burnaby', label: 'Patio cover contractor — Burnaby' },
      { path: '/patio-covers-surrey', label: 'Patio covers — Surrey' },
      { path: '/patio-covers-port-coquitlam', label: 'Patio covers — Port Coquitlam' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/projects/burnaby-aluminum-patio-cover', label: 'Burnaby aluminum project' },
      { path: '/projects/vancouver-glass-patio-cover', label: 'Vancouver glass project' },
    ],
  },
  'patio-cover-cost': {
    id: 'patio-cover-cost',
    path: '/patio-cover-cost-vancouver',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Vancouver — Get Your Price in 60 Seconds | LoomiHome Patios',
    metaDescription:
      'What drives patio cover cost in Vancouver? Skip the guesswork — design your cover online and see your price in 60 seconds, then book a free on-site measurement.',
    h1: 'Patio Cover Cost in Vancouver',
    intro:
      'If you are researching patio cover cost in Vancouver, you have probably seen numbers all over the map. That is normal — size, product type, attachment, and finish level all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price?',
        body: 'Size (square footage or span), whether you choose aluminum, glass, or a combo system, how the structure ties into your home, height and engineering needs, and any custom details (lighting, ceiling finish, etc.) all matter. Coastal weather also means we spec materials that last — that can differ from bargain online kits.',
      },
      {
        h2: 'Why a “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses your city and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
      {
        h2: 'Why local pricing is different from online kits',
        body: 'Online kits can be useful for comparison, but Vancouver-area installs need to account for rain, wind exposure, attachment conditions, access, and finishing details. A local installed price includes more than just panels in a box.',
      },
      {
        h2: 'What to prepare for a better estimate',
        body: 'A rough width, projection from the house, city, and one or two photos are enough to make the first ballpark more useful. Exact measurements come later during the free site visit.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your size and location.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Vancouver?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'Are sunrooms more expensive than patio covers?',
        a: PRICING_COPY.sunroomMoreExpensive,
      },
      {
        q: 'What factors affect aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Finished glass patio cover on a Vancouver-area home with black frame and tempered glass panels',
      caption:
        'Vancouver-area glass patio cover example — bright rain protection without losing natural light. Compare aluminum and glass in the Design & Price tool before measurement.',
      projectPath: '/projects/vancouver-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-contractors-near-me', label: 'Patio cover contractors near me' },
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/glass-patio-covers-vancouver', label: 'Glass patio covers in Vancouver' },
      { path: '/glass-vs-aluminum-patio-covers', label: 'Glass vs aluminum guide' },
      { path: '/projects/vancouver-glass-patio-cover', label: 'Vancouver glass project' },
      { path: '/projects/burnaby-aluminum-patio-cover', label: 'Burnaby aluminum project' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
      { path: '/patio-cover-cost-abbotsford', label: 'Patio cover cost in Abbotsford' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
      { path: '/patio-cover-cost-langley', label: 'Patio cover cost in Langley' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
      { path: '/patio-cover-cost-delta', label: 'Patio cover cost in Delta' },
      { path: '/patio-cover-cost-north-vancouver', label: 'Patio cover cost in North Vancouver' },
      { path: '/patio-cover-cost-west-vancouver', label: 'Patio cover cost in West Vancouver' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/patio-cover-cost-white-rock', label: 'Patio cover cost in White Rock' },
      { path: '/patio-cover-cost-pitt-meadows', label: 'Patio cover cost in Pitt Meadows' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
    ],
  },
  'glass-vs-aluminum': {
    id: 'glass-vs-aluminum',
    path: '/glass-vs-aluminum-patio-covers',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Glass vs Aluminum Covers | Vancouver | LoomiHome',
    metaDescription:
      'Glass vs aluminum patio cover for Vancouver homes. Compare light, rain protection, maintenance, and budget. Get a fast rough estimate for both in the online Design & Price tool.',
    h1: 'Glass vs Aluminum Patio Covers',
    intro:
      'Choosing between a glass patio cover and an aluminum patio cover is less about “which is best in the world” and more about what you want from the space. Aluminum leans practical: strong rain protection, lower maintenance, often friendlier on budget. Glass leans bright: more natural light, a premium modern look, often a higher investment. In Vancouver’s grey months, both can work — the right pick depends on how you use the patio and what you want to spend.',
    sections: [
      {
        h2: 'Aluminum patio covers — when they shine',
        body: 'Great when rain protection and simplicity top the list. Aluminum systems shed water well, need little upkeep, and fit a wide range of home styles. Popular across Burnaby, Surrey, and coastal-facing lots where durability matters.',
      },
      {
        h2: 'Glass patio covers — when they shine',
        body: 'Ideal when you want the patio to feel open and bright without giving up a roof. Tempered glass systems are built for durability, but expect a higher price point than basic aluminum.',
      },
      {
        h2: 'Skyline combo — the middle path',
        body: 'Some homeowners want both light and shade panels. Our skyline-style combo blends glass with V-panel sections — worth comparing alongside pure glass or pure aluminum.',
      },
      {
        h2: 'Budget and maintenance differences',
        body: 'Aluminum is usually the simpler, lower-maintenance choice. Glass often costs more and needs occasional cleaning, but it keeps the patio and nearby rooms brighter.',
      },
      {
        h2: 'How to choose for Vancouver weather',
        body: 'If rain protection is the main goal, start with aluminum or combo. If keeping daylight is just as important, compare glass at the same approximate size so the trade-off is based on numbers instead of guesswork.',
      },
    ],
    pricingNote: PRICING_COPY.glassAluminumSameSize,
    faqs: [
      {
        q: 'Which patio cover type is best?',
        a: 'The best type matches your light, budget, and maintenance comfort. We help you compare quickly instead of guessing from photos online.',
      },
      {
        q: 'Is glass harder to maintain in rainy Vancouver?',
        a: 'Glass needs occasional cleaning like any exterior glass. Aluminum roofs are lower-touch. We explain real-world upkeep when you compare.',
      },
      {
        q: 'How much more does glass cost than aluminum?',
        a: PRICING_COPY.glassVsAluminumShort,
      },
      {
        q: 'How can I compare aluminum and glass estimates online?',
        a: 'Yes. Share city and rough dimensions once — we can ballpark aluminum, glass, and skyline combo from the same measurements.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Glass patio cover on a Coquitlam-area home showing bright natural light under a tempered glass roof',
      caption:
        'Glass patio cover — keeps the patio bright while adding rain protection. Compare aluminum on the same dimensions in the Design & Price tool to see the planning range difference.',
      projectPath: '/projects/coquitlam-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/glass-patio-covers-vancouver', label: 'Glass patio covers in Vancouver' },
      { path: '/aluminum-patio-covers-vancouver', label: 'Aluminum patio covers in Vancouver' },
      { path: '/projects/vancouver-aluminum-patio-cover', label: 'Vancouver aluminum project' },
      { path: '/projects/vancouver-glass-patio-cover', label: 'Vancouver glass project' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
      { path: '/skyline-combo-patio-covers-vancouver', label: 'Skyline combo covers' },
    ],
  },
  permit: {
    id: 'permit',
    path: '/do-you-need-a-permit-for-a-patio-cover-in-vancouver',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Permits Vancouver | LoomiHome Patios',
    metaDescription:
      'Do you need a permit for a patio cover in Vancouver or BC? General guidance on when permits apply and how to plan your patio cover project.',
    h1: 'Do You Need a Permit for a Patio Cover in Vancouver?',
    intro:
      'Permit rules depend on your municipality, how the cover attaches to the home, height, projection, and sometimes whether there is existing work on the property. We are a patio cover team — not your city hall — so we cannot stamp “yes” or “no” for every lot. What we can do is build to compliant details once requirements are known, and help you move quickly on pricing while you check local rules.',
    sections: [
      {
        h2: 'Where to verify (do not skip this)',
        body: 'Contact your city’s building department or review their published guidelines for accessory structures and roof projections. Vancouver, Burnaby, Surrey, and other Lower Mainland municipalities do not all use identical thresholds.',
      },
      {
        h2: 'Why homeowners still start with us first',
        body: 'Most people want to know if the project is financially realistic before spending hours on paperwork. Our fast rough estimate gives you that orientation; permitting runs parallel when you decide to proceed.',
      },
      {
        h2: 'Details that can affect permit requirements',
        body: 'Attachment to the house, projection, height, lot coverage, drainage, and whether the cover is enclosed can all matter. A simple open cover and an enclosed sunroom may be treated very differently.',
      },
      {
        h2: 'How the site visit helps',
        body: 'During measurement we can document dimensions, attachment points, and site conditions so you have clearer information when speaking with your municipality or reviewing next steps.',
      },
    ],
    pricingNote:
      'Permit or not, the measurement visit is how we finalize structure details and price accurately.',
    faqs: [
      {
        q: 'Do I need a permit for a patio cover in BC?',
        a: 'Often yes for attached structures or larger projections, but it varies by city and design. Always confirm with your municipality.',
      },
      {
        q: 'Can LoomiHome help with permits?',
        a: 'We focus on design, product, and installation coordination. For permit questions, your city’s guidance is authoritative — we align construction details to what they require.',
      },
      {
        q: 'Should I get a price estimate before checking permits?',
        a: 'Most homeowners do — an online price tells you if the project is financially realistic while you verify municipal rules in parallel.',
      },
      {
        q: 'Do permit rules differ between Vancouver and Surrey?',
        a: 'Yes. Attachment, projection, height, and enclosure level can be treated differently. Always confirm with your city’s building department.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
      { path: '/how-long-does-patio-cover-installation-take', label: 'Installation timeline guide' },
      { path: '/patio-cover-contractor-vancouver', label: 'Patio cover contractor — Vancouver' },
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/patio-covers-burnaby', label: 'Patio covers in Burnaby' },
      { path: '/projects/burnaby-aluminum-patio-cover', label: 'Burnaby aluminum project' },
    ],
    caseStudy: {
      image: '/house/before-after/delta-aluminum-after.png',
      alt: 'Aluminum patio cover after installation on a Lower Mainland suburban backyard patio',
      caption:
        'Attached aluminum patio cover — typical open-roof project where municipal rules depend on projection and attachment. Use our Design & Price tool for a patio cover planning range, then confirm permits with your city.',
      projectPath: '/projects/delta-aluminum-patio-cover',
    },
  },
  rain: {
    id: 'rain',
    path: '/best-patio-cover-for-rain-vancouver',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Best Patio Cover for Rain | Vancouver | LoomiHome',
    metaDescription:
      'Best patio cover for rainy Vancouver weather — aluminum, glass, drainage, and durability. Compare options with an online planning range from our Design & Price tool.',
    h1: 'Best Patio Cover for Rain in Vancouver',
    intro:
      'Vancouver’s rainy season is long. The “best” patio cover for rain is one that sheds water reliably, attaches securely to your home, and uses materials that will not fall apart after a few coastal winters. For many homeowners, that means a quality aluminum roof system or a well-engineered glass roof — not a flimsy big-box kit. We help you compare options fast so you are not guessing from marketing photos.',
    sections: [
      {
        h2: 'Why drainage and attachment matter as much as the panel',
        body: 'Rain performance is not only the surface material — it is gutters, slope, flashing, and how the structure loads against your house. That is why final pricing and engineering details happen after a proper site visit.',
      },
      {
        h2: 'Aluminum vs glass in wet months',
        body: 'Solid aluminum-style roofs are the straightforward rain shield. Glass roofs still shed water when engineered correctly, with the bonus of light. Combo systems split the difference.',
      },
      {
        h2: 'Do not ignore gutters and slope',
        body: 'A patio cover that looks good but drains poorly can create new problems. We look at where water should move, how it leaves the roof, and whether the house connection is appropriate for the space.',
      },
      {
        h2: 'Best value for most rain-first projects',
        body: 'For homeowners who mainly want furniture, doors, and walkways drier, aluminum is often the practical starting point. Glass and combo systems are worth comparing when light and appearance are higher priorities.',
      },
    ],
    pricingNote:
      'Tell us your priorities — dry floor space vs maximum light — and we will ballpark the right family of products.',
    faqs: [
      {
        q: 'Do patio covers really help with Vancouver rain?',
        a: 'A properly built cover keeps furniture drier and makes the patio usable on misty days. It is one of the most common reasons people call us.',
      },
      {
        q: 'What is the fastest way to compare rain-ready options?',
        a: 'Use the online estimate — we will talk through aluminum, glass, and combo directions for your size and city.',
      },
      {
        q: 'How much does a rain-ready aluminum cover cost in Vancouver?',
        a: PRICING_COPY.aluminumOnly,
      },
      {
        q: 'Is glass still good for Vancouver rain?',
        a: 'Yes when engineered with proper slope and drainage — it sheds water while keeping the patio brighter than a solid roof.',
      },
    ],
    relatedPageLinks: [
      { path: '/glass-vs-aluminum-patio-covers', label: 'Glass vs aluminum guide' },
      { path: '/aluminum-patio-covers-vancouver', label: 'Aluminum patio covers in Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/projects/burnaby-aluminum-patio-cover', label: 'Burnaby aluminum project' },
      { path: '/projects/vancouver-glass-patio-cover', label: 'Vancouver glass project' },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Burnaby aluminum patio cover providing rain protection over a backyard seating area',
      caption:
        'Burnaby aluminum patio cover — rain-first protection for a typical Lower Mainland backyard. A practical starting point before comparing glass.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
  },
  'install-timeline': {
    id: 'install-timeline',
    path: '/how-long-does-patio-cover-installation-take',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/skyline/skyline-hero.png',
    metaTitle: 'Patio Cover Install Timeline | Vancouver | LoomiHome',
    metaDescription:
      'Patio cover installation timeline — typical phases from estimate to completion in Metro Vancouver. Plan your project with a fast rough quote first.',
    h1: 'How Long Does Patio Cover Installation Take?',
    intro:
      'Most homeowners care about two timelines: how long until they understand price, and how long until the cover is installed. We compress the first part — you can get a rough patio cover estimate in about a minute in the online Design & Price tool. After that, a free site visit locks measurements and scope. Install timing then depends on product, season, and how busy the schedule is — we give realistic ranges once your project is defined.',
    sections: [
      {
        h2: 'Phase 1 — fast ballpark (same day)',
        body: 'Online estimate with approximate size and location. No site visit required yet.',
      },
      {
        h2: 'Phase 2 — measurement & final quote',
        body: 'We visit, measure, confirm attachment and product choice, then provide firm pricing and next steps.',
      },
      {
        h2: 'Phase 3 — order & install',
        body: 'Lead times vary by product and season. We communicate scheduling clearly once you approve the quote.',
      },
      {
        h2: 'What can slow the project down',
        body: 'Custom sizes, material availability, weather windows, permit requirements, and unusual attachment conditions can all affect timing. The earlier we understand the site, the easier it is to give a realistic schedule.',
      },
      {
        h2: 'How to speed up the first step',
        body: 'Send approximate dimensions, city, product preference, and photos if you have them. That gives us enough context to ballpark quickly before booking a site visit.',
      },
    ],
    pricingNote:
      'Wondering if the project fits your timeline and budget? Start with chat — it is the fastest way to answer the budget side.',
    faqs: [
      {
        q: 'How long does patio cover installation take on site?',
        a: 'Many residential covers take a modest number of days on site once materials arrive, but scope drives everything. We give a realistic window after measurement.',
      },
      {
        q: 'Can I get an estimate today?',
        a: 'Yes — the rough estimate is designed to happen immediately in the online Design & Price tool.',
      },
      {
        q: 'What happens between online estimate and installation?',
        a: 'Free on-site measurement locks dimensions and scope, then you approve final pricing before materials are ordered and install is scheduled.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractors-near-me', label: 'Patio cover contractors near me' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
      { path: '/do-you-need-a-permit-for-a-patio-cover-in-vancouver', label: 'Patio cover permit guide' },
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/projects/surrey-skyline-combo-patio-cover', label: 'Surrey skyline combo project' },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Surrey skyline combo patio cover after installation on a residential backyard',
      caption:
        'Surrey skyline combo install — online price first, measurement second, then scheduling once materials arrive. Typical flow for combo and glass projects.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
  },
  'fraser-valley': {
    id: 'fraser-valley',
    path: '/fraser-valley-patio-covers',
    datePublished: '2026-07-31',
    dateModified: '2026-07-31',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Fraser Valley Patio Covers | Abbotsford & Pitt Meadows | LoomiHome',
    metaDescription:
      'Fraser Valley patio covers — Abbotsford, Pitt Meadows, Maple Ridge, and nearby communities. Compare aluminum, glass, and combo covers with a fast rough estimate.',
    h1: 'Fraser Valley Patio Covers — Abbotsford, Pitt Meadows & Nearby',
    intro:
      'Fraser Valley homeowners searching for patio covers often have larger backyards, seasonal rain, and a mix of suburban and semi-rural lots. Whether you are in Abbotsford, Pitt Meadows, Maple Ridge, or nearby communities, the process is the same: get an online patio cover planning range from our Design & Price tool. Sunroom pricing is confirmed after a free on-site measurement of the buildable area and site conditions.',
    sections: [
      {
        h2: 'Why Fraser Valley patios are different from city lots',
        body: 'Many Fraser Valley homes have wider spans, more yard access, and different wind exposure than compact Vancouver lots. That can change post placement, drainage, and which product type makes sense first.',
      },
      {
        h2: 'Aluminum vs glass vs combo in the Valley',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass is worth comparing when you want more light. Skyline combo covers sit in the middle when you want both shade and brightness without a full-glass budget.',
      },
      {
        h2: 'Abbotsford and Clearbrook considerations',
        body: 'Larger suburban lots in Clearbrook, Sumas Mountain, and Downtown Abbotsford often support wider patio footprints. The online Design & Price tool helps you compare planning ranges aluminum and combo options before anyone visits the property.',
      },
      {
        h2: 'Pitt Meadows and Maple Ridge spans',
        body: 'Wider backyards can make combo and aluminum layouts especially useful. Enter approximate dimensions in the Design & Price tool to compare planning ranges for multiple product directions.',
      },
      {
        h2: 'Estimate first, measure second',
        body: 'We designed the flow so you understand budget range before booking visits. Formal pricing always follows a free on-site measurement.',
      },
    ],
    pricingNote:
      'Share your Fraser Valley city, neighbourhood, and rough patio size in our Design & Price tool for a planning range before free measurement.',
    faqs: [
      {
        q: 'Do you install patio covers in Abbotsford?',
        a: 'Yes — Clearbrook, Sumas Mountain, and Downtown Abbotsford are regular Fraser Valley service areas.',
      },
      {
        q: 'Do you serve Pitt Meadows and Maple Ridge?',
        a: 'Yes — both are part of our Lower Mainland coverage with the same online estimate flow.',
      },
      {
        q: 'What is a typical aluminum patio cover cost in the Fraser Valley?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Can I compare aluminum and glass for the same patio?',
        a: 'Yes. The online Design & Price tool compares both options using the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-abbotsford', label: 'Patio covers in Abbotsford' },
      { path: '/patio-cover-contractor-abbotsford', label: 'Patio cover contractor in Abbotsford' },
      { path: '/patio-cover-cost-abbotsford', label: 'Patio cover cost in Abbotsford' },
      { path: '/patio-covers-pitt-meadows', label: 'Patio covers in Pitt Meadows' },
      { path: '/patio-cover-cost-pitt-meadows', label: 'Patio cover cost in Pitt Meadows' },
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/projects/abbotsford-aluminum-patio-cover', label: 'Abbotsford project example' },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Fraser Valley skyline combo patio cover — planning reference for larger suburban lots',
      caption:
        'Fraser Valley-style combo cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/abbotsford-aluminum-patio-cover',
    },
  },
  coastal: {
    id: 'coastal',
    path: '/coastal-patio-covers-vancouver',
    datePublished: '2026-08-04',
    dateModified: '2026-08-04',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Coastal Patio Covers Vancouver | White Rock & West Van | LoomiHome',
    metaDescription:
      'Coastal patio covers for White Rock, West Vancouver, Delta, and South Surrey — wind, rain, and light considerations. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Coastal Patio Covers — White Rock, West Vancouver & South Surrey',
    intro:
      'Coastal patio covers face a different set of questions than inland city lots: ocean exposure, wind, compact seaside decks, and the need to keep living areas bright. Whether you are in White Rock, West Vancouver, Delta, or nearby South Surrey, start with an online planning range from our Design & Price tool, compare aluminum and glass, then book a free measurement when the range fits.',
    sections: [
      {
        h2: 'Wind and rain on coastal patios',
        body: 'Coastal homes often need proper slope, attachment, and drainage — not just a roof overhead. We confirm those details during measurement rather than guessing from photos alone.',
      },
      {
        h2: 'Glass vs aluminum near the water',
        body: 'Glass is popular when homeowners want rain protection without darkening walkout decks and rear windows. Aluminum is usually the practical budget option when rain coverage matters most.',
      },
      {
        h2: 'White Rock and South Surrey decks',
        body: 'Compact seaside lots in White Rock and nearby South Surrey often need careful post placement and product choice. Chat helps narrow aluminum vs glass before a site visit.',
      },
      {
        h2: 'West Vancouver and North Shore exposure',
        body: 'West Vancouver and North Shore homes may balance view lines, wind, and premium finishes. Glass and combo options are worth comparing when light is a priority.',
      },
      {
        h2: 'Delta and Tsawwassen coastal considerations',
        body: 'Delta patios can see coastal wind and rain exposure similar to other waterfront communities. Share your neighbourhood and rough size for a useful first ballpark.',
      },
    ],
    pricingNote:
      'Enter your coastal city, approximate patio size, and cover type in the Design & Price tool to compare planning ranges.',
    faqs: [
      {
        q: 'Do you install patio covers in White Rock?',
        a: 'Yes — East Beach, West Beach, and nearby South Surrey are regular service areas.',
      },
      {
        q: 'Do you serve West Vancouver for patio covers?',
        a: 'Yes — Ambleside, Dundarave, and the British Properties are part of our North Shore coverage.',
      },
      {
        q: 'Is glass practical for coastal rain and wind?',
        a: 'Yes when engineered with proper slope, flashing, and drainage. We confirm those details on site.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-white-rock', label: 'Patio covers in White Rock' },
      { path: '/patio-covers-west-vancouver', label: 'Patio covers in West Vancouver' },
      { path: '/patio-covers-north-vancouver', label: 'Patio covers in North Vancouver' },
      { path: '/patio-cover-cost-north-vancouver', label: 'Patio cover cost in North Vancouver' },
      { path: '/patio-cover-cost-west-vancouver', label: 'Patio cover cost in West Vancouver' },
      { path: '/patio-cover-cost-white-rock', label: 'Patio cover cost in White Rock' },
      { path: '/glass-patio-covers-white-rock', label: 'Glass patio covers in White Rock' },
      { path: '/projects/white-rock-glass-patio-cover', label: 'White Rock glass project' },
      { path: '/projects/west-vancouver-glass-patio-cover', label: 'West Vancouver glass project' },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Glass patio cover on a coastal Metro Vancouver home with natural light under tempered glass roof',
      caption:
        'Coastal glass patio cover reference — compare glass and aluminum on your deck size in the online Design & Price tool before free measurement.',
      projectPath: '/projects/white-rock-glass-patio-cover',
    },
  },
  'tri-cities': {
    id: 'tri-cities',
    path: '/tri-cities-patio-covers',
    datePublished: '2026-08-26',
    dateModified: '2026-08-26',
    heroImage: '/house/before-after/coquitlam-glass-after.png',
    metaTitle: 'Tri-Cities Patio Covers | Coquitlam Port Moody PoCo | LoomiHome',
    metaDescription:
      'Tri-Cities patio covers — Coquitlam, Port Coquitlam, and Port Moody. Compare aluminum, glass, and combo covers with a fast rough estimate before free measurement.',
    h1: 'Tri-Cities Patio Covers — Coquitlam, Port Coquitlam & Port Moody',
    intro:
      'Tri-Cities homeowners searching for patio covers often deal with hillside decks, compact townhome patios, and wet springs that keep outdoor spaces unused. Whether you are in Coquitlam, Port Coquitlam, or Port Moody, the process is the same: get an online patio cover planning range from our Design & Price tool. Sunroom pricing is confirmed after a free on-site measurement of the buildable area and site conditions.',
    sections: [
      {
        h2: 'Why Tri-Cities patios need local planning',
        body: 'Burke Mountain slopes, Port Coquitlam family yards, and Port Moody walkout decks do not share the same attachment or drainage conditions. An online price uses your city and size first; measurement confirms the layout.',
      },
      {
        h2: 'Aluminum vs glass vs combo in the Tri-Cities',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass is worth comparing when you want more light near rear doors and windows. Skyline combo covers sit in the middle when you want both shade and brightness.',
      },
      {
        h2: 'Coquitlam — Burke Mountain, Maillardville, Westwood Plateau',
        body: 'Hillside decks and varied elevations are common. Share approximate dimensions and a photo so the first estimate reflects attachment height and span.',
      },
      {
        h2: 'Port Coquitlam and Port Moody neighbourhoods',
        body: 'Citadel Heights, Mary Hill, Moody Centre, and Suter Brook each have different lot depths and view lines. Chat helps you compare products before anyone visits.',
      },
      {
        h2: 'Estimate first, measure second',
        body: 'We designed the flow so you understand budget range before booking visits. Formal pricing always follows a free on-site measurement.',
      },
    ],
    pricingNote:
      'Share your Tri-Cities city, neighbourhood, and rough patio size in our Design & Price tool for a planning range before free measurement.',
    faqs: [
      {
        q: 'Do you install patio covers in Coquitlam?',
        a: 'Yes — Burke Mountain, Maillardville, Westwood Plateau, and nearby areas are regular Tri-Cities service areas.',
      },
      {
        q: 'Do you serve Port Coquitlam and Port Moody?',
        a: 'Yes — both are part of our Lower Mainland coverage with the same online estimate flow.',
      },
      {
        q: 'What is a typical aluminum patio cover cost in the Tri-Cities?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Can I compare aluminum and glass for the same patio?',
        a: 'Yes. The online Design & Price tool compares both options using the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
      { path: '/patio-covers-new-westminster', label: 'Patio covers in New Westminster' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
      { path: '/patio-covers-port-coquitlam', label: 'Patio covers in Port Coquitlam' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/patio-covers-port-moody', label: 'Patio covers in Port Moody' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
      { path: '/projects/coquitlam-glass-patio-cover', label: 'Coquitlam glass project' },
      { path: '/projects/coquitlam-aluminum-patio-cover', label: 'Coquitlam aluminum project' },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Glass patio cover on a Coquitlam Tri-Cities backyard patio',
      caption:
        'Tri-Cities glass patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/coquitlam-glass-patio-cover',
    },
  },
  'patio-cover-cost-abbotsford': {
    id: 'patio-cover-cost-abbotsford',
    path: '/patio-cover-cost-abbotsford',
    datePublished: '2026-08-05',
    dateModified: '2026-08-05',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Abbotsford | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Abbotsford? What drives price in the Fraser Valley, typical ranges, and how to get a fast rough estimate before free measurement.',
    h1: 'Patio Cover Cost in Abbotsford',
    intro:
      'If you are researching patio cover cost in Abbotsford, you have probably seen numbers that do not match your lot. Fraser Valley homes often have wider spans, larger backyards, and different attachment conditions than compact Vancouver patios — and that changes the planning range. The fastest way to get oriented is a rough online estimate for your actual size: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Abbotsford?',
        body: 'Size and span, product type (aluminum, glass, or skyline combo), attachment to the home, post placement, slope, and finish level all matter. Larger Clearbrook and Sumas Mountain lots can support wider footprints, which changes layout and engineering compared to townhome patios.',
      },
      {
        h2: 'Fraser Valley pricing vs Vancouver city lots',
        body: 'Abbotsford installs often involve more yard access and wider spans than dense city lots. That can change post count and material direction even when the square footage looks similar on paper.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Abbotsford and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
      {
        h2: 'What to prepare for a better Abbotsford estimate',
        body: 'A rough width, projection from the house, neighbourhood, and one or two photos are enough to make the first ballpark more useful. Exact measurements come later during the free site visit.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Abbotsford size and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Abbotsford?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Abbotsford?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Pitt Meadows and Maple Ridge?',
        a: 'Yes — both are part of our Fraser Valley coverage with the same online estimate flow.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Fraser Valley aluminum patio cover — planning reference for Abbotsford lots',
      caption:
        'Abbotsford-style aluminum patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/abbotsford-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-abbotsford', label: 'Patio covers in Abbotsford' },
      { path: '/patio-cover-contractor-abbotsford', label: 'Patio cover contractor in Abbotsford' },
      { path: '/projects/abbotsford-aluminum-patio-cover', label: 'Abbotsford aluminum project' },
      { path: '/projects/abbotsford-glass-patio-cover', label: 'Abbotsford glass project' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
    ],
  },
  'patio-cover-cost-surrey': {
    id: 'patio-cover-cost-surrey',
    path: '/patio-cover-cost-surrey',
    datePublished: '2026-08-18',
    dateModified: '2026-08-18',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Surrey | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Surrey? Price factors for Guildford, Cloverdale, and South Surrey lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Surrey',
    intro:
      'If you are researching patio cover cost in Surrey, you have probably seen generic numbers that do not match Guildford townhome patios or wider Cloverdale and South Surrey backyards. Size, product type, and lot layout all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Surrey?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home, post placement, slope, and finish level all matter. Surrey includes compact townhome patios and wide detached-home yards — those two layouts rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Guildford, Cloverdale, Newton, and South Surrey',
        body: 'Neighbourhood layout changes access, span, and how much cover you actually need. South Surrey and Cloverdale lots often have room for wider footprints; Newton and Guildford can be tighter. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Surrey pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep the patio and rear rooms bright. Skyline combo sits in the middle for larger Surrey backyards that want both shade and light.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Surrey and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Surrey size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Surrey?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Surrey?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve White Rock and Langley?',
        a: 'Yes — White Rock, South Surrey, Langley, and nearby Lower Mainland communities use the same online estimate flow.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover on a large Surrey backyard with glass and V-panel sections',
      caption:
        'Surrey skyline combo reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
      { path: '/patio-cover-contractor-surrey', label: 'Patio cover contractor in Surrey' },
      { path: '/aluminum-patio-covers-surrey', label: 'Aluminum patio covers in Surrey' },
      { path: '/projects/surrey-aluminum-patio-cover', label: 'Surrey aluminum project' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
    ],
  },
  'patio-cover-cost-burnaby': {
    id: 'patio-cover-cost-burnaby',
    path: '/patio-cover-cost-burnaby',
    datePublished: '2026-08-20',
    dateModified: '2026-08-20',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Burnaby | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Burnaby? Price factors for Metrotown, Brentwood, and Deer Lake lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Burnaby',
    intro:
      'If you are researching patio cover cost in Burnaby, you have probably seen numbers that do not match tight Metrotown lots or roomier Deer Lake backyards. Size, product type, and attachment conditions all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Burnaby?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home, post placement, slope, and finish level all matter. Burnaby includes compact city yards near Metrotown and wider suburban spaces near Deer Lake — those layouts rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Metrotown, Brentwood, Edmonds, and Deer Lake',
        body: 'Neighbourhood layout changes access, span, and how much cover you actually need. Tight lots often need careful post placement; larger yards can support wider footprints. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Burnaby pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep the patio and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Burnaby and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Burnaby size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Burnaby?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Burnaby?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Vancouver and Coquitlam?',
        a: 'Yes — Vancouver, Coquitlam, and nearby Metro Vancouver communities use the same online estimate flow.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Aluminum patio cover on a Burnaby backyard patio for rain protection',
      caption:
        'Burnaby aluminum patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-burnaby', label: 'Patio covers in Burnaby' },
      { path: '/patio-cover-contractor-burnaby', label: 'Patio cover contractor in Burnaby' },
      { path: '/aluminum-patio-covers-burnaby', label: 'Aluminum patio covers in Burnaby' },
      { path: '/glass-patio-covers-burnaby', label: 'Glass patio covers in Burnaby' },
      { path: '/projects/burnaby-glass-patio-cover', label: 'Burnaby glass project' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
      { path: '/patio-cover-cost-langley', label: 'Patio cover cost in Langley' },
    ],
  },
  'patio-cover-cost-langley': {
    id: 'patio-cover-cost-langley',
    path: '/patio-cover-cost-langley',
    datePublished: '2026-08-21',
    dateModified: '2026-08-21',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Langley | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Langley? Price factors for Willoughby, Walnut Grove, and Brookswood lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Langley',
    intro:
      'If you are researching patio cover cost in Langley, you have probably seen generic numbers that do not match newer Willoughby walkout decks or wider Walnut Grove and Brookswood backyards. Size, product type, and attachment conditions all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Langley?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home or deck, post placement, slope, and finish level all matter. Langley includes townhome patios and generous detached-home yards — those layouts rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Willoughby, Walnut Grove, Murrayville, and Brookswood',
        body: 'Neighbourhood layout changes access, span, and how much cover you actually need. Newer subdivisions often have walkout decks and larger rear windows; established lots can support wider footprints. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Langley pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep the deck and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Langley and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Langley size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Langley?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Langley?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Surrey and Abbotsford?',
        a: 'Yes — Surrey, Abbotsford, and nearby Fraser Valley communities use the same online estimate flow.',
      },
    ],
    caseStudy: {
      image: '/house/Aluminum/p27.jpg',
      alt: 'Aluminum patio cover on a Langley family backyard patio',
      caption:
        'Langley aluminum patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/langley-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-langley', label: 'Patio covers in Langley' },
      { path: '/patio-cover-contractor-langley', label: 'Patio cover contractor in Langley' },
      { path: '/aluminum-patio-covers-langley', label: 'Aluminum patio covers in Langley' },
      { path: '/glass-patio-covers-langley', label: 'Glass patio covers in Langley' },
      { path: '/projects/langley-glass-patio-cover', label: 'Langley glass project' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
    ],
  },
  'patio-cover-cost-richmond': {
    id: 'patio-cover-cost-richmond',
    path: '/patio-cover-cost-richmond',
    datePublished: '2026-08-25',
    dateModified: '2026-08-25',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Richmond | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Richmond? Price factors for Steveston, Broadmoor, and East Cambie lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Richmond',
    intro:
      'If you are researching patio cover cost in Richmond, you have probably seen numbers that do not match compact City Centre patios or wider Steveston and Broadmoor backyards. Size, product type, coastal exposure, and whether you need a backyard cover or a carport-style side cover all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Richmond?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home, post placement, slope, and finish level all matter. Richmond also includes long narrow side-driveway covers that price differently than square backyard patios — even when the city name is the same.',
      },
      {
        h2: 'Steveston, Broadmoor, East Cambie, and Burkeville',
        body: 'Neighbourhood layout changes access, span, and how much cover you actually need. Flat lots near the water can still see wind and rain exposure. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Richmond pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep the patio and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Richmond and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Richmond size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Richmond?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Richmond?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Vancouver and Delta?',
        a: 'Yes — Vancouver, Delta, and nearby Metro Vancouver communities use the same online estimate flow.',
      },
    ],
    caseStudy: {
      image: '/house/Aluminum/p27.jpg',
      alt: 'Aluminum patio cover on a Richmond backyard patio',
      caption:
        'Richmond aluminum patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/richmond-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-richmond', label: 'Patio covers in Richmond' },
      { path: '/patio-cover-contractor-richmond', label: 'Patio cover contractor in Richmond' },
      { path: '/aluminum-patio-covers-richmond', label: 'Aluminum patio covers in Richmond' },
      { path: '/glass-patio-covers-richmond', label: 'Glass patio covers in Richmond' },
      { path: '/projects/richmond-glass-patio-cover', label: 'Richmond glass project' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
    ],
  },
  'patio-cover-cost-coquitlam': {
    id: 'patio-cover-cost-coquitlam',
    path: '/patio-cover-cost-coquitlam',
    datePublished: '2026-08-26',
    dateModified: '2026-08-26',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Coquitlam | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Coquitlam? Price factors for Burke Mountain, Maillardville, and Tri-Cities lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Coquitlam',
    intro:
      'If you are researching patio cover cost in Coquitlam, you have probably seen numbers that do not match hillside Burke Mountain decks or more compact Maillardville and Austin Heights patios. Size, product type, slope, and attachment conditions all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Coquitlam?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home or deck, post placement, slope, and finish level all matter. Coquitlam includes hillside lots and flatter Tri-Cities yards — those layouts rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Burke Mountain, Maillardville, Westwood Plateau, and Austin Heights',
        body: 'Neighbourhood layout changes access, height, and how much cover you actually need. Sloped lots often need careful attachment planning; wider yards can support larger footprints. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Coquitlam pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep the patio and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Coquitlam and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Coquitlam size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Coquitlam?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Coquitlam?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Port Coquitlam and Port Moody?',
        a: 'Yes — both Tri-Cities neighbours use the same online estimate flow. See our Tri-Cities patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Glass patio cover on a Coquitlam backyard patio',
      caption:
        'Coquitlam patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/coquitlam-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
      { path: '/patio-cover-contractor-coquitlam', label: 'Patio cover contractor in Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
      { path: '/aluminum-patio-covers-coquitlam', label: 'Aluminum patio covers in Coquitlam' },
      { path: '/glass-patio-covers-coquitlam', label: 'Glass patio covers in Coquitlam' },
      { path: '/projects/coquitlam-aluminum-patio-cover', label: 'Coquitlam aluminum project' },
      { path: '/projects/coquitlam-glass-patio-cover', label: 'Coquitlam glass project' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'patio-cover-cost-delta': {
    id: 'patio-cover-cost-delta',
    path: '/patio-cover-cost-delta',
    datePublished: '2026-08-27',
    dateModified: '2026-08-27',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Delta | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Delta? Price factors for Ladner, Tsawwassen, and North Delta lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Delta',
    intro:
      'If you are researching patio cover cost in Delta, you have probably seen numbers that do not match compact North Delta patios or wider Ladner and Tsawwassen backyards. Size, product type, coastal exposure, and attachment conditions all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Delta?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home, post placement, slope, and finish level all matter. Delta includes compact side-yard covers and family backyards — those layouts rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Ladner, Tsawwassen, and North Delta',
        body: 'Neighbourhood layout changes access, wind exposure, and how much cover you actually need. Coastal-adjacent lots may need careful drainage planning; inland North Delta yards can support wider footprints. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Delta pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep the patio and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Delta and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Delta size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Delta?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Delta?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Richmond and White Rock?',
        a: 'Yes — Richmond, White Rock, and nearby coastal communities use the same online estimate flow. See our coastal patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/delta-aluminum-after.png',
      alt: 'Aluminum patio cover on a Delta backyard patio',
      caption:
        'Delta aluminum patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/delta-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-delta', label: 'Patio covers in Delta' },
      { path: '/patio-cover-contractor-delta', label: 'Patio cover contractor in Delta' },
      { path: '/aluminum-patio-covers-delta', label: 'Aluminum patio covers in Delta' },
      { path: '/glass-patio-covers-delta', label: 'Glass patio covers in Delta' },
      { path: '/projects/delta-glass-patio-cover', label: 'Delta glass project' },
      { path: '/projects/delta-aluminum-patio-cover', label: 'Delta aluminum project' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'patio-cover-cost-north-vancouver': {
    id: 'patio-cover-cost-north-vancouver',
    path: '/patio-cover-cost-north-vancouver',
    datePublished: '2026-08-31',
    dateModified: '2026-08-31',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost North Vancouver | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in North Vancouver? Price factors for Lynn Valley, Lower Lonsdale, and Edgemont sloped lots, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in North Vancouver',
    intro:
      'If you are researching patio cover cost in North Vancouver, you have probably seen numbers that do not match a compact Lower Lonsdale deck or a wider Lynn Valley backyard. Size, product type, slope, tree exposure, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in North Vancouver?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, slope and post placement, attachment to the home, and finish level all matter. North Shore lots rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Lynn Valley, Lower Lonsdale, and Edgemont',
        body: 'Neighbourhood layout changes access, elevation, and wind exposure. Mountainside decks may need taller posts and careful drainage; flatter Lower Lonsdale patios can support different spans. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on North Shore pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep walkout decks and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses North Vancouver and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your North Vancouver size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in North Vancouver?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in North Vancouver?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve West Vancouver and Vancouver?',
        a: 'Yes — West Vancouver, Vancouver, and nearby North Shore communities use the same online estimate flow. See our coastal patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Aluminum patio cover on a sloped North Shore backyard patio',
      caption:
        'North Vancouver aluminum patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/north-vancouver-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-north-vancouver', label: 'Patio covers in North Vancouver' },
      { path: '/patio-cover-contractor-north-vancouver', label: 'Patio cover contractor in North Vancouver' },
      { path: '/aluminum-patio-covers-north-vancouver', label: 'Aluminum patio covers in North Vancouver' },
      { path: '/glass-patio-covers-north-vancouver', label: 'Glass patio covers in North Vancouver' },
      { path: '/projects/north-vancouver-glass-patio-cover', label: 'North Vancouver glass project' },
      { path: '/projects/north-vancouver-aluminum-patio-cover', label: 'North Vancouver aluminum project' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
      { path: '/patio-cover-cost-west-vancouver', label: 'Patio cover cost in West Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'patio-cover-cost-west-vancouver': {
    id: 'patio-cover-cost-west-vancouver',
    path: '/patio-cover-cost-west-vancouver',
    datePublished: '2026-09-01',
    dateModified: '2026-09-01',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Patio Cover Cost West Vancouver | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in West Vancouver? Price factors for Ambleside, Dundarave, and British Properties decks, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in West Vancouver',
    intro:
      'If you are researching patio cover cost in West Vancouver, you have probably seen numbers that do not match a compact Ambleside deck or a wider British Properties walkout. Size, product type, coastal exposure, view lines, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in West Vancouver?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, coastal wind exposure, attachment to the home, and finish level all matter. West Vancouver lots rarely price the same even when the neighbourhood name is identical.',
      },
      {
        h2: 'Ambleside, Dundarave, and the British Properties',
        body: 'Neighbourhood layout changes access, elevation, and view priorities. Walkout decks may need taller posts and careful drainage; flatter Ambleside patios can support different spans. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on West Vancouver pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep walkout decks and rear rooms bright while preserving views. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses West Vancouver and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your West Vancouver size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in West Vancouver?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in West Vancouver?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve North Vancouver and Vancouver?',
        a: 'Yes — North Vancouver, Vancouver, and nearby North Shore communities use the same online estimate flow. See our coastal patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Glass patio cover on a premium West Coast home with natural light under tempered glass roof',
      caption:
        'West Vancouver glass patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/west-vancouver-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-west-vancouver', label: 'Patio covers in West Vancouver' },
      { path: '/patio-cover-contractor-west-vancouver', label: 'Patio cover contractor in West Vancouver' },
      { path: '/glass-patio-covers-west-vancouver', label: 'Glass patio covers in West Vancouver' },
      { path: '/aluminum-patio-covers-west-vancouver', label: 'Aluminum patio covers in West Vancouver' },
      { path: '/projects/west-vancouver-aluminum-patio-cover', label: 'West Vancouver aluminum project' },
      { path: '/projects/west-vancouver-glass-patio-cover', label: 'West Vancouver glass project' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
      { path: '/patio-cover-cost-north-vancouver', label: 'Patio cover cost in North Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'patio-cover-cost-new-westminster': {
    id: 'patio-cover-cost-new-westminster',
    path: '/patio-cover-cost-new-westminster',
    datePublished: '2026-09-01',
    dateModified: '2026-09-01',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost New Westminster | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in New Westminster? Price factors for Queensborough, Sapperton, and Uptown townhome patios, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in New Westminster',
    intro:
      'If you are researching patio cover cost in New Westminster, you have probably seen numbers that do not match a compact Queensborough townhome patio or a wider Sapperton backyard. Size, product type, attachment height, and layout all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in New Westminster?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, attachment to the home, and finish level all matter. New Westminster patios range from compact townhome layouts to detached backyards, so identical city names can price differently.',
      },
      {
        h2: 'Queensborough, Sapperton, and Uptown',
        body: 'Neighbourhood layout changes access and usable patio depth. Townhome patios may need tighter post placement; detached backyards can support wider spans. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on New Westminster pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses New Westminster and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your New Westminster size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in New Westminster?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in New Westminster?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Burnaby and Coquitlam?',
        a: 'Yes — Burnaby, Coquitlam, and nearby Metro Vancouver communities use the same online estimate flow. See our Tri-Cities patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Lower Mainland aluminum patio cover — planning reference for compact New Westminster patios',
      caption:
        'Lower Mainland aluminum patio cover example used for New Westminster cost planning — compare aluminum, combo, and glass on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/new-westminster-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-new-westminster', label: 'Patio covers in New Westminster' },
      { path: '/patio-cover-contractor-new-westminster', label: 'Patio cover contractor in New Westminster' },
      { path: '/aluminum-patio-covers-new-westminster', label: 'Aluminum patio covers in New Westminster' },
      { path: '/glass-patio-covers-new-westminster', label: 'Glass patio covers in New Westminster' },
      { path: '/projects/new-westminster-skyline-combo-cover', label: 'New Westminster combo project' },
      { path: '/projects/new-westminster-aluminum-patio-cover', label: 'New Westminster aluminum project' },
      { path: '/projects/new-westminster-glass-patio-cover', label: 'New Westminster glass project' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
    ],
  },
  'patio-cover-cost-maple-ridge': {
    id: 'patio-cover-cost-maple-ridge',
    path: '/patio-cover-cost-maple-ridge',
    datePublished: '2026-09-02',
    dateModified: '2026-09-02',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Maple Ridge | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Maple Ridge? Price factors for Town Centre, Albion, and Silver Valley backyards, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Maple Ridge',
    intro:
      'If you are researching patio cover cost in Maple Ridge, you have probably seen numbers that do not match a compact Town Centre patio or a wider Silver Valley backyard. Size, product type, span, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Maple Ridge?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, post placement, attachment to the home, and finish level all matter. Maple Ridge lots rarely price the same even when the neighbourhood name is identical.',
      },
      {
        h2: 'Town Centre, Albion, and Silver Valley',
        body: 'Neighbourhood layout changes access and usable patio depth. Larger Silver Valley and Albion backyards can support wider spans; tighter Town Centre patios may need different post placement. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Maple Ridge pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget — a popular fit on wider Maple Ridge lots.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Maple Ridge and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Maple Ridge size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Maple Ridge?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Maple Ridge?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Pitt Meadows and Abbotsford?',
        a: 'Yes — Pitt Meadows, Abbotsford, and nearby Fraser Valley communities use the same online estimate flow. See our Fraser Valley patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Fraser Valley skyline combo patio cover — planning reference for Maple Ridge lots',
      caption:
        'Maple Ridge skyline combo patio cover reference — compare aluminum, combo, and glass on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/patio-cover-contractor-maple-ridge', label: 'Patio cover contractor in Maple Ridge' },
      { path: '/aluminum-patio-covers-maple-ridge', label: 'Aluminum patio covers in Maple Ridge' },
      { path: '/projects/maple-ridge-aluminum-patio-cover', label: 'Maple Ridge aluminum project' },
      { path: '/projects/maple-ridge-glass-patio-cover', label: 'Maple Ridge glass project' },
      { path: '/patio-covers-pitt-meadows', label: 'Patio covers in Pitt Meadows' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
      { path: '/patio-cover-cost-pitt-meadows', label: 'Patio cover cost in Pitt Meadows' },
      { path: '/patio-cover-cost-langley', label: 'Patio cover cost in Langley' },
    ],
  },
  'patio-cover-cost-white-rock': {
    id: 'patio-cover-cost-white-rock',
    path: '/patio-cover-cost-white-rock',
    datePublished: '2026-09-02',
    dateModified: '2026-09-02',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Patio Cover Cost White Rock | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in White Rock? Price factors for East Beach, West Beach, and South Surrey coastal decks, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in White Rock',
    intro:
      'If you are researching patio cover cost in White Rock, you have probably seen numbers that do not match a compact East Beach deck or a wider South Surrey backyard. Size, product type, coastal exposure, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in White Rock?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, coastal wind exposure, attachment to the home, and finish level all matter. White Rock and nearby South Surrey lots rarely price the same even when the neighbourhood name is identical.',
      },
      {
        h2: 'East Beach, West Beach, and South Surrey',
        body: 'Neighbourhood layout changes access, ocean exposure, and usable patio depth. Compact seaside decks may need careful post placement; roomier South Surrey yards can support wider spans. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on White Rock pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep decks and rear rooms bright despite coastal weather. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses White Rock and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your White Rock size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in White Rock?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in White Rock?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Surrey and Delta?',
        a: 'Yes — Surrey, Delta, and nearby South Surrey communities use the same online estimate flow. See our coastal patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Glass patio cover on a coastal Metro Vancouver home with natural light under tempered glass roof',
      caption:
        'White Rock glass patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/white-rock-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-white-rock', label: 'Patio covers in White Rock' },
      { path: '/patio-cover-contractor-white-rock', label: 'Patio cover contractor in White Rock' },
      { path: '/glass-patio-covers-white-rock', label: 'Glass patio covers in White Rock' },
      { path: '/projects/white-rock-aluminum-patio-cover', label: 'White Rock aluminum project' },
      { path: '/projects/white-rock-glass-patio-cover', label: 'White Rock glass project' },
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
      { path: '/patio-cover-cost-delta', label: 'Patio cover cost in Delta' },
    ],
  },
  'patio-cover-cost-pitt-meadows': {
    id: 'patio-cover-cost-pitt-meadows',
    path: '/patio-cover-cost-pitt-meadows',
    datePublished: '2026-09-03',
    dateModified: '2026-09-03',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Pitt Meadows | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Pitt Meadows? Price factors for wider Fraser Valley backyards, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Pitt Meadows',
    intro:
      'If you are researching patio cover cost in Pitt Meadows, you have probably seen numbers that do not match a compact side patio or a wider suburban backyard. Size, product type, span, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Pitt Meadows?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, post placement, attachment to the home, and finish level all matter. Pitt Meadows lots rarely price the same even when the city name is identical.',
      },
      {
        h2: 'Wider Fraser Valley layouts',
        body: 'Many Pitt Meadows homes have room for wider patio spans than city yards. Share approximate width and projection so the first ballpark is useful before free measurement.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Pitt Meadows pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Pitt Meadows and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Pitt Meadows size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Pitt Meadows?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Pitt Meadows?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Maple Ridge and Abbotsford?',
        a: 'Yes — Maple Ridge, Abbotsford, and nearby Fraser Valley communities use the same online estimate flow. See our Fraser Valley patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Aluminum patio cover on a Fraser Valley suburban backyard',
      caption:
        'Pitt Meadows aluminum patio cover reference — compare aluminum, combo, and glass on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/pitt-meadows-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-pitt-meadows', label: 'Patio covers in Pitt Meadows' },
      { path: '/patio-cover-contractor-pitt-meadows', label: 'Patio cover contractor in Pitt Meadows' },
      { path: '/aluminum-patio-covers-pitt-meadows', label: 'Aluminum patio covers in Pitt Meadows' },
      { path: '/projects/pitt-meadows-aluminum-patio-cover', label: 'Pitt Meadows aluminum project' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
    ],
  },
  'patio-cover-cost-port-coquitlam': {
    id: 'patio-cover-cost-port-coquitlam',
    path: '/patio-cover-cost-port-coquitlam',
    datePublished: '2026-09-03',
    dateModified: '2026-09-03',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Cover Cost Port Coquitlam | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Port Coquitlam? Price factors for Citadel Heights, Mary Hill, and Lincoln Park patios, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Port Coquitlam',
    intro:
      'If you are researching patio cover cost in Port Coquitlam, you have probably seen numbers that do not match a compact townhome patio or a wider Mary Hill backyard. Size, product type, slope, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Port Coquitlam?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, hillside slope, attachment to the home, and finish level all matter. Port Coquitlam lots rarely price the same even when the neighbourhood name is identical.',
      },
      {
        h2: 'Citadel Heights, Mary Hill, and Lincoln Park',
        body: 'Neighbourhood layout changes access and usable patio depth. Townhomes may need tighter post placement; detached hillside yards can support different spans. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Port Coquitlam pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep walkout decks and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Port Coquitlam and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Port Coquitlam size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Port Coquitlam?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Port Coquitlam?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Coquitlam and Port Moody?',
        a: 'Yes — Coquitlam, Port Moody, and the wider Tri-Cities use the same online estimate flow. See our Tri-Cities patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Glass patio cover on a Tri-Cities backyard patio',
      caption:
        'Port Coquitlam glass patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/port-coquitlam-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-port-coquitlam', label: 'Patio covers in Port Coquitlam' },
      { path: '/patio-cover-contractor-port-coquitlam', label: 'Patio cover contractor in Port Coquitlam' },
      { path: '/aluminum-patio-covers-port-coquitlam', label: 'Aluminum patio covers in Port Coquitlam' },
      { path: '/projects/port-coquitlam-aluminum-patio-cover', label: 'Port Coquitlam aluminum project' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
  },
  'patio-cover-cost-port-moody': {
    id: 'patio-cover-cost-port-moody',
    path: '/patio-cover-cost-port-moody',
    datePublished: '2026-09-03',
    dateModified: '2026-09-03',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Patio Cover Cost Port Moody | LoomiHome Patios',
    metaDescription:
      'How much does a patio cover cost in Port Moody? Price factors for Moody Centre, Suter Brook, and Newport Village decks, typical ranges, and a fast estimate.',
    h1: 'Patio Cover Cost in Port Moody',
    intro:
      'If you are researching patio cover cost in Port Moody, you have probably seen numbers that do not match a compact Moody Centre deck or a wider Suter Brook walkout. Size, product type, coastal exposure, and attachment height all move the total. The fastest way to get oriented is a planning range from our online Design & Price tool for your patio: about a minute, no site visit yet. Then, if the range makes sense, we book a free measurement and give you firm numbers.',
    sections: [
      {
        h2: 'What usually drives patio cover price in Port Moody?',
        body: 'Span and square footage, whether you choose aluminum, glass, or skyline combo, coastal rain exposure, attachment to the home, and finish level all matter. Port Moody lots rarely price the same even when the neighbourhood name is identical.',
      },
      {
        h2: 'Moody Centre, Suter Brook, and Newport Village',
        body: 'Neighbourhood layout changes access and usable patio depth. Compact inlet-facing decks may need careful post placement; roomier yards can support wider spans. Share the neighbourhood with approximate dimensions so the first ballpark is useful.',
      },
      {
        h2: 'Aluminum vs glass vs combo on Port Moody pricing',
        body: 'Aluminum is usually the rain-first starting point for practical budget. Glass costs more when you want to keep walkout decks and rear rooms bright. Skyline combo sits in the middle when you want both shade and light without a full-glass budget.',
      },
      {
        h2: 'Why a generic “starting from” number is not enough',
        body: 'Starting prices are useful for orientation, but your patio is not generic. A ballpark that uses Port Moody and approximate dimensions is far more actionable — which is why we built the fast estimate flow first.',
      },
      {
        h2: 'Rough quote first, accurate quote second',
        body: 'We believe you should understand budget range before spending an evening on sales appointments. Use the Design & Price tool for a planning range, then book a free on-site visit when you want line-item clarity and a measured final price.',
      },
    ],
    pricingNote:
      'Use the Design & Price tool to compare cover types for your Port Moody size, neighbourhood, and product direction.',
    faqs: [
      {
        q: 'How much does a patio cover usually cost in Port Moody?',
        a: PRICING_COPY.threeWayCompare,
      },
      {
        q: 'Is the online estimate binding?',
        a: 'It is a planning range only. Your formal quote is always confirmed after free on-site measurement and site review.',
      },
      {
        q: 'What factors affect aluminum patio cover cost in Port Moody?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you also serve Port Coquitlam and Coquitlam?',
        a: 'Yes — Port Coquitlam, Coquitlam, and the wider Tri-Cities use the same online estimate flow. See our Tri-Cities patio covers guide for nearby examples.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Glass patio cover on a compact Tri-Cities patio',
      caption:
        'Port Moody glass patio cover reference — compare aluminum, glass, and combo on your dimensions in the online Design & Price tool before free measurement.',
      projectPath: '/projects/port-moody-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-port-moody', label: 'Patio covers in Port Moody' },
      { path: '/patio-cover-contractor-port-moody', label: 'Patio cover contractor in Port Moody' },
      { path: '/glass-patio-covers-port-moody', label: 'Glass patio covers in Port Moody' },
      { path: '/projects/port-moody-aluminum-patio-cover', label: 'Port Moody aluminum project' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
  },
  'showroom-vancouver': {
    id: 'showroom-vancouver',
    path: '/patio-cover-showroom-vancouver',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-main.jpg',
    metaTitle: 'Patio Cover Showroom Vancouver | LoomiHome Patios',
    metaDescription:
      'Looking for a patio cover showroom near Vancouver? Visit the LoomiHome Patios showroom in Burnaby, BC — full-size aluminum, glass, and skyline combo patio cover displays plus an all-glass sunroom.',
    h1: 'Patio Cover Showroom Near Vancouver',
    intro:
      'Many homeowners want to see real patio covers before they commit — the frame finish, glass, roof panels, and how everything fits together. LoomiHome Patios has its own showroom in Burnaby, BC, a short drive from Vancouver, with full-size displays of our aluminum, glass, and skyline combo patio covers and an all-glass sunroom. Start with an online planning range from our Design & Price tool, then book a showroom visit or a free on-site measurement.',
    sections: [
      {
        h2: 'Why a real showroom matters',
        body: 'Photos online can make every patio cover look the same. Standing under a full-size display helps you judge quality, colour, light, and scale before you choose a product direction.',
      },
      {
        h2: 'What is on display',
        body: 'Full-size aluminum patio covers, glass patio covers, skyline combo covers (glass plus V-panel), and an all-glass sunroom. Our team can explain how each option handles Vancouver rain, light, and maintenance.',
      },
      {
        h2: 'Showroom plus warehouse stock',
        body: 'We keep aluminum profile inventory in our own warehouse. That helps with planning and scheduling compared with companies that only resell or subcontract.',
      },
      {
        h2: 'Estimate first, then visit or measure',
        body: 'Most homeowners start with a rough online estimate using their city and approximate patio size. If the range fits, book a showroom visit in Burnaby or a free on-site measurement for a formal quote.',
      },
    ],
    pricingNote:
      'Pricing is based on your patio size, product, and site details — not on a showroom display. Use our Design & Price tool to see the price for your size, then confirm with a free measurement.',
    faqs: [
      {
        q: 'Do you have a patio cover showroom in the Vancouver area?',
        a: 'Yes — LoomiHome Patios has its own showroom in Burnaby, BC, with full-size patio cover and sunroom displays. Book a showroom visit through the website or chat.',
      },
      {
        q: 'Can I see aluminum and glass patio covers before ordering?',
        a: 'Yes. The Burnaby showroom has full-size aluminum, glass, and skyline combo displays, so you can compare them side by side.',
      },
      {
        q: 'Do I need to visit the showroom to get a price?',
        a: 'No. Use the Design & Price tool for a patio cover planning range in about a minute. A free on-site measurement confirms formal pricing.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/showroom/patio-cover-displays.jpg',
      alt: 'Patio cover displays in the LoomiHome Burnaby showroom',
      caption: 'Full-size patio cover displays in our Burnaby showroom — compare aluminum, glass, and skyline combo in person.',
      projectPath: '/projects/vancouver-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/aluminum-patio-cover-showroom-vancouver', label: 'Aluminum patio cover showroom' },
      { path: '/glass-patio-cover-showroom-vancouver', label: 'Glass patio cover showroom' },
      { path: '/sunroom-showroom-vancouver', label: 'Sunroom showroom' },
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'showroom-near-me': {
    id: 'showroom-near-me',
    path: '/patio-cover-showroom-near-me',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-space.jpg',
    metaTitle: 'Patio Cover Showroom Near Me | Burnaby, BC | LoomiHome',
    metaDescription:
      'Searching for a patio cover showroom near you? The LoomiHome Patios showroom in Burnaby, BC serves Vancouver, Surrey, Richmond, Coquitlam, Langley, and the Lower Mainland with full-size patio cover and sunroom displays.',
    h1: 'Patio Cover Showroom Near Me — Burnaby, BC',
    intro:
      'If you are searching for a patio cover showroom near you, you probably want a real local company — not a lead form that sells your details. LoomiHome Patios has its own showroom in Burnaby, BC, centrally located for the Lower Mainland, plus our own warehouse stock. Get an online planning range from our Design & Price tool, then book a showroom visit or a free measurement at home.',
    sections: [
      {
        h2: 'One Burnaby showroom for the Lower Mainland',
        body: 'Our Burnaby showroom team serves Vancouver, Burnaby, New Westminster, Richmond, Surrey, Delta, White Rock, Langley, Coquitlam, Port Coquitlam, Port Moody, Maple Ridge, Pitt Meadows, Abbotsford, North Vancouver, and West Vancouver.',
      },
      {
        h2: 'How to tell a real showroom from a lead site',
        body: 'Look for photos of an actual showroom and warehouse, real local projects, and a team that measures and installs directly. Ask who will be on site and where materials are stocked.',
      },
      {
        h2: 'Compare full-size displays in one place',
        body: 'Full-size aluminum, glass, and skyline combo patio covers and an all-glass sunroom are on display, so you can compare finishes, light, and structure side by side before choosing.',
      },
      {
        h2: 'Skip the drive if you want',
        body: 'You do not have to visit first. Many homeowners start with an online estimate, then book a free on-site measurement and see samples during that visit.',
      },
    ],
    pricingNote:
      'Share your city and approximate patio size in our Design & Price tool for a planning range. Final pricing follows a free on-site measurement.',
    faqs: [
      {
        q: 'Is there a patio cover showroom near me?',
        a: 'LoomiHome Patios has a showroom in Burnaby, BC serving the wider Lower Mainland. Book a showroom visit through the website or chat.',
      },
      {
        q: 'Which cities do you serve from your showroom?',
        a: 'Vancouver, Burnaby, Richmond, Surrey, Delta, White Rock, Langley, Coquitlam, Port Coquitlam, Port Moody, New Westminster, Maple Ridge, Pitt Meadows, Abbotsford, North Vancouver, and West Vancouver.',
      },
      {
        q: 'Can you bring samples to my home?',
        a: 'Material details can be discussed during the free on-site measurement. Mention in chat what you want to compare.',
      },
    ],
    caseStudy: {
      image: '/house/company/warehouse-2.png',
      alt: 'LoomiHome Patios aluminum profile inventory in warehouse',
      caption: 'Aluminum profile inventory in our own warehouse — real local stock, not a lead-generation site.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-vancouver', label: 'Patio cover showroom near Vancouver' },
      { path: '/patio-cover-contractors-near-me', label: 'Patio cover contractors near me' },
      { path: '/aluminum-patio-cover-showroom-vancouver', label: 'Aluminum patio cover showroom' },
      { path: '/glass-patio-cover-showroom-vancouver', label: 'Glass patio cover showroom' },
      { path: '/sunroom-showroom-vancouver', label: 'Sunroom showroom' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers' },
    ],
  },
  'aluminum-showroom': {
    id: 'aluminum-showroom',
    path: '/aluminum-patio-cover-showroom-vancouver',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Aluminum Patio Cover Showroom Vancouver | LoomiHome',
    metaDescription:
      'See full-size aluminum patio cover displays at the LoomiHome Patios showroom in Burnaby, BC — profiles, finishes, and roof panels. Quick planning range from our online Design & Price tool and free on-site measurement.',
    h1: 'Aluminum Patio Cover Showroom — Burnaby, BC',
    intro:
      'Aluminum is the most common starting point for Vancouver-area patio covers because it handles rain well, needs little maintenance, and is friendlier on budget. Our Burnaby showroom has a full-size aluminum patio cover on display, so you can check profiles, post sizes, and finishes in person. LoomiHome Patios also keeps aluminum profile stock in our own warehouse.',
    sections: [
      {
        h2: 'What to look at in person',
        body: 'Beam and post profiles, wall thickness, powder-coat finish, roof panel options, gutters, and how the cover attaches to the house. These details are easier to judge in person than in photos.',
      },
      {
        h2: 'Why warehouse stock helps',
        body: 'Keeping aluminum profiles in our own warehouse supports planning and scheduling. It also means you can see the same materials that will be used on your project.',
      },
      {
        h2: 'Aluminum vs glass vs combo',
        body: 'Aluminum leans practical and rain-first. Glass keeps more light. Skyline combo mixes both. Compare all three on the same patio size in the Design & Price tool before choosing.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, size, and aluminum cover selection to see a planning range. A free on-site measurement confirms the final number.',
    faqs: [
      {
        q: 'Can I see aluminum patio cover materials before ordering?',
        a: 'Yes — our Burnaby showroom has a full-size aluminum patio cover display, and we keep aluminum profile stock in our own warehouse. Book a showroom visit through the website or chat.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
      {
        q: 'Do you install aluminum patio covers across the Lower Mainland?',
        a: 'Yes — Vancouver, Burnaby, Surrey, Richmond, Coquitlam, Langley, and nearby cities are regular service areas.',
      },
    ],
    caseStudy: {
      image: '/house/company/warehouse-2.png',
      alt: 'Aluminum patio cover profile inventory at LoomiHome Patios warehouse',
      caption: 'Aluminum profile inventory — the same materials used on our patio cover installs.',
      projectPath: '/projects/vancouver-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-vancouver', label: 'Patio cover showroom near Vancouver' },
      { path: '/aluminum-patio-covers-vancouver', label: 'Aluminum patio covers in Vancouver' },
      { path: '/aluminum-patio-covers-burnaby', label: 'Aluminum patio covers in Burnaby' },
      { path: '/aluminum-patio-covers-surrey', label: 'Aluminum patio covers in Surrey' },
      { path: '/glass-vs-aluminum-patio-covers', label: 'Glass vs aluminum guide' },
    ],
  },
  'glass-showroom': {
    id: 'glass-showroom',
    path: '/glass-patio-cover-showroom-vancouver',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Glass Patio Cover Showroom Vancouver | LoomiHome',
    metaDescription:
      'See full-size glass and skyline combo patio cover displays at the LoomiHome Patios showroom in Burnaby, BC. Compare light, frames, and finishes, then use our Design & Price tool for a patio cover planning range.',
    h1: 'Glass Patio Cover Showroom — Burnaby, BC',
    intro:
      'Glass patio covers keep living areas bright while blocking Vancouver rain, but they are a bigger investment than aluminum. Many homeowners want to stand under one before deciding. Our Burnaby showroom has full-size glass and skyline combo patio covers on display, next to our aluminum cover, so you can compare light and finish side by side.',
    sections: [
      {
        h2: 'What to compare for glass covers',
        body: 'Tempered glass panels, frame colour and profile, slope, drainage details, and how the cover looks against your house. Seeing these in person makes the light-versus-budget decision easier.',
      },
      {
        h2: 'Glass or skyline combo?',
        body: 'Skyline combo mixes glass and V-panel sections for both light and shade at a lower cost than full glass. It is worth comparing if you like glass but want more shade or a smaller budget.',
      },
      {
        h2: 'Estimate before you visit',
        body: 'Use the Design & Price tool to compare glass and aluminum planning ranges for your patio size. Then see materials in person or book a free on-site measurement.',
      },
    ],
    pricingNote:
      'Glass usually costs more than aluminum on the same footprint. Our Design & Price tool gives both ranges from your approximate size.',
    faqs: [
      {
        q: 'Can I see glass patio cover options in person?',
        a: 'Yes — our Burnaby showroom has full-size glass and skyline combo patio cover displays. Book a showroom visit through the website or chat.',
      },
      {
        q: 'How much more does glass cost than aluminum?',
        a: PRICING_COPY.aluminumGlassCompare,
      },
      {
        q: 'Do glass covers work in Vancouver rain?',
        a: 'Yes, when engineered with proper slope, flashing, and drainage. We confirm those details during the free measurement.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Glass patio cover on a Vancouver home with natural light under tempered glass',
      caption: 'Vancouver glass patio cover — compare glass and aluminum on your size in the online Design & Price tool.',
      projectPath: '/projects/vancouver-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-vancouver', label: 'Patio cover showroom near Vancouver' },
      { path: '/glass-patio-covers-vancouver', label: 'Glass patio covers in Vancouver' },
      { path: '/glass-patio-covers-burnaby', label: 'Glass patio covers in Burnaby' },
      { path: '/skyline-combo-patio-covers-vancouver', label: 'Skyline combo covers in Vancouver' },
      { path: '/glass-vs-aluminum-patio-covers', label: 'Glass vs aluminum guide' },
    ],
  },
  'sunroom-showroom': {
    id: 'sunroom-showroom',
    path: '/sunroom-showroom-vancouver',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/sunroom-display.jpg',
    metaTitle: 'Sunroom Showroom Vancouver | Burnaby, BC | LoomiHome',
    metaDescription:
      'Looking for a sunroom showroom near Vancouver? Walk through a full-size all-glass sunroom at the LoomiHome Patios showroom in Burnaby, BC. Book a free on-site measurement to review buildable area and confirm pricing.',
    h1: 'Sunroom Showroom Near Vancouver — Burnaby, BC',
    intro:
      'A sunroom is a bigger project than a patio cover, so it makes sense to step inside one before deciding. Our Burnaby showroom has a full-size all-glass sunroom on display, alongside our patio cover displays. Sunroom pricing is confirmed after a free on-site measurement — book a showroom visit or a free on-site measurement.',
    sections: [
      {
        h2: 'What to compare for a sunroom',
        body: 'Frame profiles, glass and wall panel options, roof style, doors, and how the enclosure connects to your existing patio or house. These choices affect comfort, light, and budget.',
      },
      {
        h2: 'Patio cover first, sunroom later?',
        body: 'Some homeowners start with a patio cover and enclose it later. Our team can explain which cover systems are easier to convert into a sunroom.',
      },
      {
        h2: 'Estimate first',
        body: 'Sunroom pricing depends on layout, glass, and foundation, so we confirm it after a free on-site measurement. Book one from our homepage or ask us in chat.',
      },
    ],
    pricingNote:
      'Sunroom pricing depends on buildable area, layout, and site conditions. We confirm it after a free on-site measurement.',
    faqs: [
      {
        q: 'Do you have a sunroom showroom in the Vancouver area?',
        a: 'Yes — our Burnaby, BC showroom has a full-size all-glass sunroom on display. Book a showroom visit through the website or ask us in chat.',
      },
      {
        q: 'Can you enclose an existing patio cover?',
        a: 'Often yes, depending on the structure. We confirm on site during the free measurement.',
      },
      {
        q: 'Which cities do you install sunrooms in?',
        a: 'Vancouver, Burnaby, Richmond, Surrey, Coquitlam, Langley, Maple Ridge, and nearby Lower Mainland cities.',
      },
    ],
    caseStudy: {
      image: '/house/showroom/showroom-main.jpg',
      alt: 'Inside the LoomiHome Burnaby showroom — patio cover displays and the all-glass sunroom',
      caption: 'Our Burnaby showroom — the all-glass sunroom and patio cover displays side by side.',
      projectPath: '/projects/burnaby-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-vancouver', label: 'Patio cover showroom near Vancouver' },
      { path: '/sunrooms-vancouver', label: 'Sunrooms in Vancouver' },
      { path: '/sunrooms-burnaby', label: 'Sunrooms in Burnaby' },
      { path: '/sunrooms-surrey', label: 'Sunrooms in Surrey' },
      { path: '/sunrooms-richmond', label: 'Sunrooms in Richmond' },
    ],
  },
  'showroom-burnaby': {
    id: 'showroom-burnaby',
    path: '/patio-cover-showroom-burnaby',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-wide-hero.jpg',
    metaTitle: 'Patio Cover Showroom Burnaby, BC | LoomiHome Patios',
    metaDescription:
      'Visit the LoomiHome Patios showroom in Burnaby, BC. Full-size aluminum, glass, and skyline combo patio cover displays plus an all-glass sunroom. Book a showroom visit or a free on-site measurement.',
    h1: 'Patio Cover & Sunroom Showroom in Burnaby, BC',
    intro:
      'Our showroom is in Burnaby, BC — central for Vancouver, New Westminster, Coquitlam, Richmond, and Surrey homeowners. Inside you can walk under full-size aluminum, glass, and skyline combo patio covers and step into an all-glass sunroom. See it in person before you decide, then book a free on-site measurement for exact pricing.',
    sections: [
      {
        h2: 'What you will see in the Burnaby showroom',
        body: 'Full-size patio cover displays in aluminum, glass, and skyline combo, plus an all-glass sunroom. Compare frame finishes, how much light each roof lets through, and how the structures look at real scale.',
      },
      {
        h2: 'Who visits the showroom',
        body: 'Homeowners comparing glass and aluminum, families planning a sunroom, and anyone who wants to meet the local team before booking an install. Burnaby neighbours in Metrotown, Brentwood, Edmonds, and Deer Lake are close by.',
      },
      {
        h2: 'Book a visit or start with an estimate',
        body: 'Use the Book a Showroom Visit button on our homepage or ask us in chat. For patio cover planning ranges, use the Design & Price tool with your city and approximate patio size.',
      },
      {
        h2: 'Local stock, local installs',
        body: 'Our own warehouse keeps aluminum profile stock, and our team measures and installs across the Lower Mainland.',
      },
    ],
    pricingNote:
      'Showroom displays show product and finish — your price depends on patio size, product, and site details. Our Design & Price tool gives a planning range; free measurement confirms it.',
    faqs: [
      {
        q: 'Where is the LoomiHome Patios showroom?',
        a: 'Our showroom is in Burnaby, BC. Book a visit through the website or chat to get directions and a time that suits you.',
      },
      {
        q: 'What can I see at the Burnaby showroom?',
        a: 'Full-size aluminum, glass, and skyline combo patio covers and an all-glass sunroom.',
      },
      {
        q: 'Do I need to visit before getting a quote?',
        a: 'No. Many homeowners start with an online estimate and a free on-site measurement. The showroom is there if you want to see products in person.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/showroom/patio-cover-displays.jpg',
      alt: 'Patio cover displays in the LoomiHome Burnaby showroom',
      caption: 'Patio cover displays in our Burnaby showroom — aluminum, glass, and skyline combo at full size.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-burnaby', label: 'Patio covers in Burnaby' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
      { path: '/patio-cover-showroom-vancouver', label: 'Patio cover showroom near Vancouver' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/sunroom-showroom-vancouver', label: 'Sunroom showroom' },
      { path: '/sunrooms-burnaby', label: 'Sunrooms in Burnaby' },
    ],
  },
  'showroom-new-westminster': {
    id: 'showroom-new-westminster',
    path: '/patio-cover-showroom-new-westminster',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-main.jpg',
    metaTitle: 'Patio Cover Showroom Near New Westminster, BC | LoomiHome Patios',
    metaDescription:
      'Looking for a patio cover showroom near New Westminster? The LoomiHome Patios showroom in Burnaby, BC is a short drive away — full-size aluminum, glass, and skyline combo displays plus an all-glass sunroom.',
    h1: 'Patio Cover Showroom Near New Westminster',
    intro:
      'New Westminster homeowners do not have to choose a patio cover from photos alone. The LoomiHome Patios showroom in nearby Burnaby, BC is a short drive from Downtown New West, Sapperton, Queensborough, and Uptown — with full-size aluminum, glass, and skyline combo patio cover displays and an all-glass sunroom you can walk through.',
    sections: [
      {
        h2: 'A short drive from New Westminster',
        body: 'Burnaby borders New Westminster directly, so the showroom is one of the closest places to see real patio covers and a sunroom at full size before you buy.',
      },
      {
        h2: 'What is on display',
        body: 'Full-size aluminum patio covers, glass patio covers, skyline combo covers (glass plus V-panel), and an all-glass sunroom. Compare frame finishes, light, and scale side by side.',
      },
      {
        h2: 'Estimate first, then visit or measure',
        body: 'Most homeowners start with an online planning range from our Design & Price tool using their city and approximate patio size. Then book a showroom visit or a free on-site measurement for formal pricing.',
      },
    ],
    pricingNote:
      'Showroom displays show product and finish — your price depends on patio size, product, and site details. Our Design & Price tool gives a planning range; free measurement confirms it.',
    faqs: [
      {
        q: 'Is there a patio cover showroom near New Westminster?',
        a: 'Yes — the LoomiHome Patios showroom in Burnaby, BC is a short drive from New Westminster, with full-size patio cover and sunroom displays.',
      },
      {
        q: 'Can I see a sunroom in person before ordering?',
        a: 'Yes. The Burnaby showroom has an all-glass sunroom you can walk through, alongside full-size patio cover displays.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/showroom/patio-cover-displays.jpg',
      alt: 'Patio cover displays in the LoomiHome Burnaby showroom',
      caption: 'Full-size patio cover displays in our Burnaby showroom — a short drive from New Westminster.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/patio-covers-new-westminster', label: 'Patio covers in New Westminster' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
      { path: '/sunroom-showroom-vancouver', label: 'Sunroom showroom' },
    ],
  },
  'showroom-coquitlam': {
    id: 'showroom-coquitlam',
    path: '/patio-cover-showroom-coquitlam',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-main.jpg',
    metaTitle: 'Patio Cover Showroom Near Coquitlam, BC | LoomiHome Patios',
    metaDescription:
      'Looking for a patio cover showroom near Coquitlam? The LoomiHome Patios showroom in Burnaby, BC is a short drive via Highway 1 — full-size aluminum, glass, and skyline combo displays plus an all-glass sunroom.',
    h1: 'Patio Cover Showroom Near Coquitlam',
    intro:
      'Coquitlam homeowners can see real patio covers before they commit. The LoomiHome Patios showroom in Burnaby, BC is a short drive west via Highway 1 or Lougheed Highway from Coquitlam Town Centre, Burke Mountain, Maillardville, and Austin Heights — with full-size aluminum, glass, and skyline combo patio cover displays and an all-glass sunroom.',
    sections: [
      {
        h2: 'A short drive from Coquitlam',
        body: 'The Burnaby showroom sits just west of Coquitlam along Highway 1, making it an easy trip for Tri-Cities homeowners comparing patio cover options.',
      },
      {
        h2: 'What is on display',
        body: 'Full-size aluminum patio covers, glass patio covers, skyline combo covers (glass plus V-panel), and an all-glass sunroom. Judge quality, colour, light, and scale in person.',
      },
      {
        h2: 'Estimate first, then visit or measure',
        body: 'Start with an online planning range from our Design & Price tool using your city and approximate patio size, then book a showroom visit or a free on-site measurement for formal pricing.',
      },
    ],
    pricingNote:
      'Showroom displays show product and finish — your price depends on patio size, product, and site details. Our Design & Price tool gives a planning range; free measurement confirms it.',
    faqs: [
      {
        q: 'Is there a patio cover showroom near Coquitlam?',
        a: 'Yes — the LoomiHome Patios showroom in Burnaby, BC is a short drive from Coquitlam via Highway 1, with full-size patio cover and sunroom displays.',
      },
      {
        q: 'Can I compare aluminum and glass covers side by side?',
        a: 'Yes. The Burnaby showroom has full-size aluminum, glass, and skyline combo displays so you can compare them directly.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/showroom/patio-cover-displays.jpg',
      alt: 'Patio cover displays in the LoomiHome Burnaby showroom',
      caption: 'Full-size patio cover displays in our Burnaby showroom — a short drive from Coquitlam.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers' },
    ],
  },
  'showroom-richmond': {
    id: 'showroom-richmond',
    path: '/patio-cover-showroom-richmond',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-main.jpg',
    metaTitle: 'Patio Cover Showroom Near Richmond, BC | LoomiHome Patios',
    metaDescription:
      'Looking for a patio cover showroom near Richmond? The LoomiHome Patios showroom in Burnaby, BC is a short drive via the Knight Street Bridge — full-size aluminum, glass, and skyline combo displays plus an all-glass sunroom.',
    h1: 'Patio Cover Showroom Near Richmond',
    intro:
      'Richmond homeowners can see patio covers and sunrooms in person before deciding. The LoomiHome Patios showroom in Burnaby, BC is a short drive from Steveston, Richmond Centre, Brighouse, and Sea Island via the Knight Street Bridge — with full-size aluminum, glass, and skyline combo patio cover displays and an all-glass sunroom you can walk through.',
    sections: [
      {
        h2: 'A short drive from Richmond',
        body: 'Cross the Knight Street Bridge and you are close to the Burnaby showroom — the nearest place for Richmond homeowners to stand under full-size patio covers and step inside a real sunroom.',
      },
      {
        h2: 'What is on display',
        body: 'Full-size aluminum patio covers, glass patio covers, skyline combo covers (glass plus V-panel), and an all-glass sunroom. Compare how each option handles rain, light, and privacy.',
      },
      {
        h2: 'Estimate first, then visit or measure',
        body: 'Get an online planning range from our Design & Price tool with your city and approximate patio size, then book a showroom visit or a free on-site measurement for formal pricing.',
      },
    ],
    pricingNote:
      'Showroom displays show product and finish — your price depends on patio size, product, and site details. Our Design & Price tool gives a planning range; free measurement confirms it.',
    faqs: [
      {
        q: 'Is there a patio cover showroom near Richmond?',
        a: 'Yes — the LoomiHome Patios showroom in Burnaby, BC is a short drive from Richmond via the Knight Street Bridge, with full-size patio cover and sunroom displays.',
      },
      {
        q: 'Do I need to visit the showroom to get a price?',
        a: 'No. Use the Design & Price tool for a patio cover planning range in about a minute. A free on-site measurement confirms formal pricing.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/showroom/patio-cover-displays.jpg',
      alt: 'Patio cover displays in the LoomiHome Burnaby showroom',
      caption: 'Full-size patio cover displays in our Burnaby showroom — a short drive from Richmond.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/patio-covers-richmond', label: 'Patio covers in Richmond' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
      { path: '/sunrooms-richmond', label: 'Sunrooms in Richmond' },
    ],
  },
  'showroom-surrey': {
    id: 'showroom-surrey',
    path: '/patio-cover-showroom-surrey',
    datePublished: '2026-10-02',
    dateModified: '2026-10-02',
    heroImage: '/house/showroom/showroom-main.jpg',
    metaTitle: 'Patio Cover Showroom Near Surrey, BC | LoomiHome Patios',
    metaDescription:
      'Looking for a patio cover showroom near Surrey? The LoomiHome Patios showroom in Burnaby, BC is a short drive over the Port Mann — full-size aluminum, glass, and skyline combo displays plus an all-glass sunroom.',
    h1: 'Patio Cover Showroom Near Surrey',
    intro:
      'Surrey homeowners can see patio covers and sunrooms at full size before they buy. The LoomiHome Patios showroom in Burnaby, BC is a short drive from Whalley, Guildford, Cloverdale, and South Surrey — with full-size aluminum, glass, and skyline combo patio cover displays and an all-glass sunroom.',
    sections: [
      {
        h2: 'A short drive from Surrey',
        body: 'The Burnaby showroom is an easy trip from Surrey for homeowners who want to compare real products — frame finishes, glass, roof panels, and scale — before choosing.',
      },
      {
        h2: 'What is on display',
        body: 'Full-size aluminum patio covers, glass patio covers, skyline combo covers (glass plus V-panel), and an all-glass sunroom. Our team can explain how each option handles rain, light, and maintenance.',
      },
      {
        h2: 'Estimate first, then visit or measure',
        body: 'Surrey lots range from compact townhome patios to wider backyards — start with an online planning range from our Design & Price tool, then book a showroom visit or a free on-site measurement for formal pricing.',
      },
    ],
    pricingNote:
      'Showroom displays show product and finish — your price depends on patio size, product, and site details. Our Design & Price tool gives a planning range; free measurement confirms it.',
    faqs: [
      {
        q: 'Is there a patio cover showroom near Surrey?',
        a: 'Yes — the LoomiHome Patios showroom in Burnaby, BC is a short drive from Surrey, with full-size patio cover and sunroom displays.',
      },
      {
        q: 'Can I see a sunroom in person before ordering?',
        a: 'Yes. The Burnaby showroom has an all-glass sunroom you can walk through, alongside full-size patio cover displays.',
      },
      {
        q: 'What is a typical aluminum patio cover cost?',
        a: PRICING_COPY.example12x14Aluminum,
      },
    ],
    caseStudy: {
      image: '/house/showroom/patio-cover-displays.jpg',
      alt: 'Patio cover displays in the LoomiHome Burnaby showroom',
      caption: 'Full-size patio cover displays in our Burnaby showroom — a short drive from Surrey.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
      { path: '/patio-cover-showroom-near-me', label: 'Patio cover showroom near me' },
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
      { path: '/sunrooms-surrey', label: 'Sunrooms in Surrey' },
    ],
  },
};
